import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { PageHeader, Empty } from "@/components/Bits";
import StatusChip from "@/components/StatusChip";
import Modal from "@/components/Modal";

const BLANK = { name: "", phone: "", email: "", address: "", gstin: "", payment_terms: "Net 30" };

export default function Vendors() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const nav = useNavigate();

  const load = async () => setList((await api.get("/vendors")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (form.id) await api.put(`/vendors/${form.id}`, form);
      else await api.post("/vendors", form);
      toast.success("Vendor saved");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (v) => setDetail((await api.get(`/vendors/${v.id}`)).data);

  return (
    <div>
      <PageHeader eyebrow="Directory" title="Vendors" subtitle="Wholesale suppliers, payment terms and amounts payable.">
        <button className="btn btn-primary" onClick={() => setForm({ ...BLANK })} data-testid="new-vendor-btn"><Plus size={16} /> New vendor</button>
      </PageHeader>

      <div className="card overflow-hidden">
        <table className="tbl">
          <thead><tr><th>Vendor</th><th>GSTIN</th><th>Phone</th><th>Terms</th><th className="num">POs</th><th className="num">Payable</th><th></th></tr></thead>
          <tbody>
            {list.map((v) => (
              <tr key={v.id} className="cursor-pointer" onClick={() => openDetail(v)} data-testid={`vendor-row-${v.id}`}>
                <td className="font-medium">{v.name}</td>
                <td className="font-num text-xs text-[#8C929B]">{v.gstin}</td>
                <td className="font-num text-[#5C626A]">{v.phone}</td>
                <td className="text-[#5C626A]">{v.payment_terms}</td>
                <td className="num font-num">{v.po_count}</td>
                <td className="num font-num" style={{ color: v.payable > 0 ? "#A3411D" : "#8C929B" }}>{inr(v.payable)}</td>
                <td className="num"><button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setForm(v); }}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <Empty>No vendors yet</Empty>}
      </div>

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Edit Vendor" : "New Vendor"} testid="vendor-form">
        {form && (
          <div className="space-y-4">
            {["name", "phone", "email", "address", "gstin"].map((f) => (
              <div key={f}>
                <label className="label">{f}</label>
                <input className="field" value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`vendor-${f}`} />
              </div>
            ))}
            <div>
              <label className="label">Payment terms</label>
              <select className="field" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}>
                {["Advance", "On delivery", "Net 7", "Net 15", "Net 30", "Net 45", "Net 60"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={!form.name} data-testid="save-vendor">Save</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name} wide testid="vendor-detail">
        {detail && (
          <div>
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div><div className="eyebrow mb-1">GSTIN</div><span className="font-num">{detail.gstin || "—"}</span></div>
              <div><div className="eyebrow mb-1">Contact</div>{detail.phone}<br />{detail.email || "—"}</div>
              <div className="col-span-2"><div className="eyebrow mb-1">Address</div>{detail.address || "—"}</div>
            </div>
            <div className="eyebrow mb-2">Purchase orders</div>
            <div className="border border-[#E6E1D8] rounded-lg overflow-auto max-h-80">
              <table className="tbl">
                <thead><tr><th>PO</th><th>Date</th><th>Bill no.</th><th className="num">Received value</th><th className="num">Paid</th><th className="num">Payable</th><th>Status</th></tr></thead>
                <tbody>
                  {detail.purchase_orders.map((p) => (
                    <tr key={p.id} className="cursor-pointer" onClick={() => nav(`/purchases?po=${p.id}`)}>
                      <td className="font-num">{p.po_number}</td><td>{fmtDate(p.order_date)}</td>
                      <td className="font-num text-xs text-[#8C929B]">{p.vendor_bill_no || "—"}</td>
                      <td className="num font-num">{inr(p.billed)}</td><td className="num font-num">{inr(p.amount_paid)}</td>
                      <td className="num font-num" style={{ color: p.payable > 0 ? "#A3411D" : "#8C929B" }}>{inr(p.payable)}</td>
                      <td><StatusChip status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {detail.purchase_orders.length === 0 && <Empty>No purchase orders</Empty>}
            </div>
            {detail.payments?.length > 0 && <>
              <div className="eyebrow mt-5 mb-2">Payments made</div>
              <div className="border border-[#E6E1D8] rounded-lg overflow-auto max-h-60">
                <table className="tbl"><tbody>
                  {detail.payments.map((p) => <tr key={p.id}><td>{fmtDate(p.date)}</td><td className="font-num text-xs">{p.po_number}</td><td className="text-[#5C626A]">{p.method}</td><td className="num font-num">{inr(p.amount)}</td></tr>)}
                </tbody></table>
              </div>
            </>}
          </div>
        )}
      </Modal>
    </div>
  );
}
